import { FaLock, FaEnvelope, FaChevronRight } from "react-icons/fa";
import {
  Button,
  Form,
  FormItem,
  Input,
  PasswordInput,
  useForm,
} from "@/frontend/components/common";
import { authService, poolInvitationService } from "@/frontend/services";
import { useAuthStore } from "@/frontend/stores";
import { useNavigate, useSearchParams } from "react-router-dom";
import type {
  ApiResponse,
  LoginRequestDTO,
  LoginResponseDTO,
} from "@/shared/dto";
import { message } from "antd";

export function LoginForm() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login } = useAuthStore();
  const [form] = useForm();
  
  const invitationToken = searchParams.get("token");

  const handleSubmit = async (values: LoginRequestDTO) => {
    try {
      const response: ApiResponse<LoginResponseDTO> =
        await authService.login(values);

      if (response.success && response.data) {
        login(
          response.data.accessToken,
          response.data.refreshToken,
          response.data.user,
          response.data.permissions,
        );

        if (invitationToken) {
          const acceptResponse = await poolInvitationService.accept({ token: invitationToken });
          if (acceptResponse.success) {
            message.success("¡Te has unido a la polla exitosamente!");
            navigate(`/sports-predictions/pools/${acceptResponse.data?.poolId}`);
          } else {
            message.warning(acceptResponse.error || "No se pudo aceptar la invitación");
            navigate("/dashboard");
          }
        } else {
          navigate("/dashboard");
        }
      } else {
        throw new Error(response.error || "Datos de acceso incorrectos.");
      }
    } catch (err) {
      form.setFields([
        {
          name: "password",
          errors: [
            err instanceof Error ? err.message : "Error de servicio",
          ],
        },
      ]);
    }
  };

  return (
    <div className="w-full">
      <Form
        form={form}
        onFinish={handleSubmit}
        layout="vertical"
        className="space-y-5"
      >
        <FormItem
          name="email"
          label={<span className="text-slate-700 text-sm font-semibold ml-1">Correo Electrónico</span>}
          rules={[
            { required: true, message: "Campo requerido" },
            { type: "email", message: "Formato inválido" },
          ]}
        >
          <Input
            prefix={<FaEnvelope className="text-slate-400 mr-2" />}
            placeholder="usuario@ejemplo.com"
            size="large"
            className="bg-slate-50 border-slate-200 text-slate-900 h-12 rounded-xl focus:border-emerald-500 focus:bg-white transition-all placeholder:text-slate-400"
          />
        </FormItem>

        <FormItem
          name="password"
          label={
            <div className="flex justify-between w-full px-1">
              <span className="text-slate-700 text-sm font-semibold">Contraseña</span>
              <button 
                type="button" 
                onClick={() => navigate("/forgot-password")}
                className="text-sm text-emerald-600 hover:text-emerald-700 font-bold transition-colors"
              >
                ¿La olvidaste?
              </button>
            </div>
          }
          rules={[
            { required: true, message: "Campo requerido" },
            { min: 8, message: "Mínimo 8 caracteres" },
          ]}
        >
          <PasswordInput
            prefix={<FaLock className="text-slate-400 mr-2" />}
            placeholder="••••••••"
            size="large"
            className="bg-slate-50 border-slate-200 text-slate-900 h-12 rounded-xl focus:border-emerald-500 focus:bg-white transition-all"
          />
        </FormItem>

        <FormItem className="pt-6">
          <Button
            type="primary"
            htmlType="submit"
            size="large"
            block
            className="h-14 bg-emerald-600 hover:bg-emerald-500 border-none rounded-xl text-lg font-bold flex items-center justify-center gap-2 shadow-lg shadow-emerald-200 text-white transition-all transform active:scale-[0.98]"
          >
            Entrar ahora <FaChevronRight className="text-sm opacity-50" />
          </Button>
        </FormItem>
      </Form>
      
      <div className="mt-10 text-center">
        <p className="text-slate-500 text-sm font-medium">
          ¿No tienes una cuenta?{" "}
          <button 
            type="button" 
            onClick={() => navigate("/register")}
            className="text-emerald-600 font-bold hover:text-emerald-700 transition-colors ml-1"
          >
            Regístrate gratis
          </button>
        </p>
      </div>
    </div>
  );
}
